import { api } from "@shared/lib/api";
import { BackednResponse, IInfluencerSuggestFacets } from "@shared/lib/types";
import { Review } from "@shared/lib/types/agency";

import {
  Influencer,
  InfluencerContact,
  CreateInfluencerRequest,
  SocialMedia,
  GetInfluencers,
  ICreateInfluencerRequest,
  IInfluencerFacets,
  ILocationFacet,
  IInfluencer,
  SocialMediaPlatform,
  SocialMediaSearch,
  SocialMediaInfluencerRequest,
  SocialMediaInfluencer,
  InfluencerRemoveContact,
  CONTACT_STATUS,
  IInfluencerContactVerificationResponse,
  IInfluencerContactVerification,
} from "@shared/lib/types/influencer";
import { ISocialMediaInfluencerV2 } from "@shared/lib/types/socialMediaInfluencerV2";

export class InfluencerService {
  public static async getInfluencers(
    searchQuery?: string,
    platform?: string[],
    location?: string[],
    rating?: number,
    category?: string[],
    type?: string[],
    limit?: number,
    pageNo?: number,
    linkedAgencies?: string[]
  ) {
    const params = new URLSearchParams();
    if (searchQuery) {
      params.append("searchQuery", searchQuery);
    }
    if (platform && platform.length) {
      params.append("platform", platform.join("$$"));
    }
    if (location && location.length) {
      params.append("location", location.join("$$"));
    }
    if (rating) {
      params.append("rating", rating.toString());
    }
    if (category && category.length) {
      params.append("category", category.join("$$"));
    }
    if (type && type.length) {
      params.append("type", type.join("$$"));
    }
    if (linkedAgencies && linkedAgencies.length) {
      params.append("linkedAgencies", linkedAgencies.join("$$"));
    }
    if (limit) {
      params.append("limit", limit.toString());
    }
    if (pageNo) {
      params.append("pageNo", pageNo.toString());
    }

    const queryString = params.toString();
    const url = `influencer/${queryString ? `?${queryString}` : ""}`;
    const influencers = (await api
      .get(url)
      .json()) as BackednResponse<GetInfluencers>;
    return influencers;
  }
  public static async getInfluencerById(id: string) {
    const url = `influencer/${id}`;
    const influencer = (await api
      .get(url)
      .json()) as BackednResponse<IInfluencer>;
    return influencer;
  }

  public static async createInfluencer(influencer: ICreateInfluencerRequest) {
    await api.post("influencer", {
      json: {
        ...influencer,
      },
    });
  }

  public static async updateInfluencer(
    influencer: Partial<IInfluencer>,
    influencerId: string
  ) {
    const { note, ...rest } = influencer;

    return (await api
      .patch(`influencer`, {
        json: {
          ...rest,
          influencerId,
        },
      })
      .json()) as BackednResponse<IInfluencer>;
  }
  public static async updateNote(note: string, influencerId: string) {
    return (await api
      .post(`influencer/note`, {
        json: {
          note,
          influencerId,
        },
      })
      .json()) as BackednResponse<Influencer>;
  }
  public static async addContact(
    influencerId: string,
    contact: Partial<InfluencerContact>
  ) {
    return (await api
      .post(`influencer/contact`, {
        json: {
          influencerId,
          contactToAdd: contact,
        },
      })
      .json()) as BackednResponse<Influencer>;
  }
  public static async updateContact(
    influencerId: string,
    contact: Partial<InfluencerContact>
  ) {
    return (await api
      .patch(`influencer/contact`, {
        json: {
          influencerId,
          contactToEdit: contact,
        },
      })
      .json()) as BackednResponse<Influencer>;
  }
  public static async editContactStatus(
    influencerId: string,
    influencerContactId: string,
    status: CONTACT_STATUS
  ) {
    return (await api
      .patch(`influencer/contact/status`, {
        json: { influencerId, influencerContactId, status },
      })
      .json()) as BackednResponse<Influencer>;
  }
  public static async removeContact(
    influencerId: string,
    contact: InfluencerRemoveContact
  ) {
    return (await api
      .delete(`influencer/contact`, {
        json: {
          influencerId,
          contactToRemove: contact,
        },
      })
      .json()) as BackednResponse<Influencer>;
  }
  public static async linkSocialMedia(
    influencerId: string,
    socialMedia: SocialMedia
  ) {
    return (await api
      .patch(`influencer/social-media-link`, {
        json: {
          influencerId,
          ...socialMedia,
        },
      })
      .json()) as BackednResponse<Influencer>;
  }
  public static async deLinkSocialMedia(
    influencerId: string,
    socialMediaLinkageId: string
  ) {
    return (await api
      .delete(`influencer/social-media-link`, {
        json: {
          influencerId,
          socialMediaLinkageId,
        },
      })
      .json()) as BackednResponse<Influencer>;
  }
  public static async linkAgency(influencerId: string, agencyId: string) {
    return (await api
      .post(`influencer/agency/link`, {
        json: {
          influencerId,
          agencyId,
        },
      })
      .json()) as BackednResponse<Influencer>;
  }
  public static async deLinkAgency(influencerId: string) {
    return (await api
      .delete(`influencer/agency/de-link?influencerId=${influencerId}`, {})
      .json()) as BackednResponse<Influencer>;
  }
  public static async getInfluencerFacets() {
    const url = `influencer/facets`;

    const users = (await api
      .get(url)
      .json()) as BackednResponse<IInfluencerFacets>;

    return users;
  }

  public static async getInfluencerRequirementsFacets() {
    const url = `brief/form/facets`;

    const users = (await api
      .get(url)
      .json()) as BackednResponse<IInfluencerFacets>;

    return users;
  }
  public static async getLocationFacets(location?: string) {
    const url = `influencer/facets/location?location=${location}`;

    const users = (await api.get(url).json()) as BackednResponse<
      ILocationFacet[]
    >;

    return users;
  }
  public static async addReview(
    influencerId: string,
    review: Pick<Review, "comment" | "review">
  ) {
    return (await api
      .post(`influencer/review`, {
        json: {
          influencerId,
          ...review,
        },
      })
      .json()) as BackednResponse<Influencer>;
  }
  public static async deleteReview(influencerId: string, reviewId: string) {
    return (await api
      .delete(`influencer/review`, {
        json: {
          influencerId,
          reviewId,
        },
      })
      .json()) as BackednResponse<Influencer>;
  }

  public static async getSocialMedias(
    searchQuery: string,
    platform: SocialMediaPlatform
  ) {
    const params = new URLSearchParams();

    params.append("username", searchQuery);

    params.append("platform", platform);

    const queryString = params.toString();
    const url = `social-media/search/public-user/${
      queryString ? `?${queryString}` : ""
    }`;
    const influencers = (await api.get(url).json()) as BackednResponse<
      SocialMediaSearch[]
    >;
    return influencers;
  }

  public static async bulkUploadInfluencers(
    inputFileUrl: string,
    bucketId?: string
  ) {
    return (await api
      .post(`download/bulkUploadInfluencers`, {
        json: {
          inputFileUrl,
          bucketId,
        },
      })
      .json()) as BackednResponse<{}>;
  }

  public static async getSocialMediaInfluencer(
    request: SocialMediaInfluencerRequest
  ) {
    return (await api
      .post(`social-media/search`, { json: request })
      .json()) as BackednResponse<SocialMediaInfluencer>;
  }

  public static async linkSocialMediaToInfluencer(request: {
    socialMediaObjectId: string;
    influencerId: string;
  }) {
    return (await api
      .patch(`influencer/social-media-link/id`, { json: request })
      .json()) as BackednResponse<SocialMediaInfluencer>;
  }

  public static async getSocialMedia(socialMediaId: string) {
    return (await api
      .get(`social-media/v2/${socialMediaId}`)
      .json()) as BackednResponse<ISocialMediaInfluencerV2>;
  }

  public static async getInfluencerSuggestFacets(briefId: string) {
    const params = new URLSearchParams();
    params.append("briefId", briefId);
    const queryString = params.toString();
    return (await api
      .get(
        `brief/influencer-suggest/facets/${
          queryString ? `?${queryString}` : ""
        }`
      )
      .json()) as BackednResponse<IInfluencerSuggestFacets>;
  }

  public static async getInfluencerContactVerification(
    pageNo: number,
    limit: number
  ) {
    const response = (await api
      .post(`influencer/contact/invalid-resolution/search`, {
        json: {
          pageNo,
          limit,
        },
      })
      .json()) as BackednResponse<IInfluencerContactVerificationResponse>;
    return response;
  }

  public static async markInfluencerContactVerificationAsResolved(
    influencerContactVerificationId: string,
    resolutionRemarks: string
  ) {
    return (await api
      .patch(`influencer/contact/invalid-resolution/mark-as-resolved`, {
        json: {
          influencerContactVerificationId,
          resolutionRemarks,
        },
      })
      .json()) as BackednResponse<IInfluencerContactVerification>;
  }

  public static async requestProfileAnalysis(socialMediaObjectId: string) {
    return (await api
      .post(`social-media/ai/request-profile-analysis`, {
        json: {
          socialMediaObjectId,
        },
      })
      .json()) as BackednResponse<string>;
  }
}
